import { create } from "zustand";

// localStorage keys shared with the Quasar app so a user can switch between
// the two UIs on the same origin without logging in again.
export const USER_KEY = "pbranks_userid";
export const IMPERSONATE_KEY = "impersonatedId";
export const TEMP_USER_KEY = "pbranks_temp_userid";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* private mode etc. */
  }
}

type SessionState = {
  userId: string | null;
  impersonateId: string | null;
  login: (guid: string) => void;
  logout: () => void;
  setImpersonate: (id: string | null) => void;
};

export const useSession = create<SessionState>((set, get) => ({
  userId: read(USER_KEY),
  impersonateId: read(IMPERSONATE_KEY),
  login: (guid) => {
    write(USER_KEY, guid);
    set({ userId: guid });
  },
  logout: () => {
    // The key of the session being ended: the push unregister below runs after
    // local state is cleared, so it must carry the credential explicitly.
    const guid = get().userId;
    // Forget this device's push target first (#385), then revoke the session
    // cookie server-side (#289) — clearing local state alone would leave a
    // valid credential in the browser. Fire-and-forget and lazily imported to
    // keep the store free of an api-client cycle.
    void import("../push/forget")
      .then((m) => m.forgetPushRegistrations(guid))
      .catch(() => {})
      .then(() => import("../api/endpoints/user"))
      .then((m) => m.userApi.logoutSession())
      .catch(() => {
        /* signing out locally still succeeds */
      });
    write(USER_KEY, null);
    write(TEMP_USER_KEY, null);
    set({ userId: null });
  },
  setImpersonate: (id) => {
    write(IMPERSONATE_KEY, id && id.trim() ? id.trim() : null);
    set({ impersonateId: id && id.trim() ? id.trim() : null });
  },
}));

export const isLoggedIn = () => !!useSession.getState().userId;

/**
 * Reconcile local state with the session cookie on boot (#289).
 *
 * Safari deletes localStorage after seven days of no interaction with the
 * site, so a returning user can have a perfectly good HttpOnly session cookie
 * and no guid. Ask the server who we are; it answers from the cookie. When we
 * DO still have a guid, the same call upgrades the browser by minting a
 * cookie, so the purge stops mattering from then on.
 *
 * Returns once the answer is known; callers hold the UI until then so the
 * auth guard cannot bounce a logged-in user to the sign-in page.
 */
export async function restoreSession(): Promise<void> {
  try {
    const { userApi } = await import("../api/endpoints/user");
    const res = await userApi.ensureSession();
    if (res?.guid) {
      const current = useSession.getState().userId;
      if (current !== res.guid) useSession.getState().login(res.guid);
    }
  } catch {
    // No cookie and no guid, or the API is unreachable: stay as we are.
  }
}
